import { toggleFieldAttributeV2 as toggleFieldAttribute } from 'commonAssets/lib/toggleFieldAttributeV2';
import { toggleForeignPhone } from 'commonAssets/lib/toggleForeignPhone.js';

const SECTION_PREFIX = ["B", "C", "D"];

/**
 * Strips parenthetical text and trims, matching the label cleaning that
 * transformTransactionKeys() (scripts/utils.js) applies on save.
 *
 * @param {string} label
 * @returns {string}
 */
function cleanLabel(label) {
    return (label || "").replace(/\s*\([^)]*\)/g, "").trim();
}

/**
 * Recomputes the saved key for a rendered transaction input, mirroring
 * transformTransactionKeys(): "<section>.<table>: <cleaned label>".
 *
 * Stored claim data uses these prefixed keys (e.g. "B.2: Total
 * Purchase/Acquisition Price", "B.1: TOTAL NUMBER OF SHARES"), but the rendered
 * inputs carry the raw `data-label` (un-prefixed, parentheticals intact), so we
 * derive the key from the input's numeric item-id + data-label to match them.
 *
 * @param {Element} input
 * @returns {string|null} The saved key, or null if the element has no numeric item-id.
 */
function transactionKey(input) {
    const itemId = parseInt(input.getAttribute("item-id"), 10);
    if (isNaN(itemId)) return null;
    const section = Math.floor(itemId / 1000);
    const table = Math.floor((itemId % 1000) / 100);
    const prefix = SECTION_PREFIX[section - 1];
    if (!prefix) return null;
    return `${prefix}.${table}: ${cleanLabel(input.getAttribute("data-label"))}`;
}

/**
 * Finds a rendered transaction input by its saved key. When `itemId` is given,
 * the search is scoped to that row; otherwise all transaction inputs are searched.
 *
 * @param {string} name - The saved key (e.g. "B.2: Total Purchase/Acquisition Price").
 * @param {number|string|null} itemId
 * @returns {Element|null}
 */
function findTransactionInput(name, itemId = null) {
    const scope = itemId != null
        ? document.querySelectorAll(`[item-id="${itemId}"]`)
        : document.querySelectorAll('.transactions input, .transactions textarea, .transactions select');
    return Array.from(scope).find((el) => transactionKey(el) === name) || null;
}

/**
 * Ensures a transaction row exists for the given numeric item-id, clicking the
 * table's "add" button as needed. Rows are appended synchronously on click, so
 * the element is available immediately after this returns true.
 *
 * @param {number} itemId
 * @returns {boolean} Whether a row with that item-id now exists.
 */
function ensureTransactionRow(itemId) {
    if (document.querySelector(`[item-id="${itemId}"]`)) return true;
    const tableNum = Math.floor((itemId % 1000) / 100);
    const addBtn = document.getElementById("add_transactions_" + tableNum);
    if (!addBtn) return false;
    let safety = 0;
    while (!document.querySelector(`[item-id="${itemId}"]`) && safety < 100) {
        addBtn.click();
        safety++;
    }
    return Boolean(document.querySelector(`[item-id="${itemId}"]`));
}

/**
 * @param {*} member
 * @param {*} config
 */
export default function loadMember(member, config) {

    const claimFields = member.claim_fields || [];
    const claimTransactions = member.claim_transactions || [];
    const claimFiles = member.claim_files || [];

    for (const field of claimFields) {
        let input = document.querySelector(`[data-label="${field.name}"]`); // RH - case radio would fail since this was a const

        // Holdings (B.1 / B.4 "TOTAL NUMBER OF SHARES") are saved into
        // claim_fields but rendered inside the first/last transaction tables,
        // keyed with a section.table prefix. Route them to that input.
        if (!input && /^[B-D]\.\d+:/.test(field.name)) {
            input = findTransactionInput(field.name);
        }

        if (!input) continue;

        switch (input.type) {
            case 'text':
            case 'tel':
            case 'email':
            case 'number':
            case 'date':
            case 'select-one':
            case 'hidden':
                input.value = field.value;
                break;
            case 'textarea':
                input.innerText = field.value;
                break;
            case 'checkbox':
                input.checked = field.value.toUpperCase() === 'YES';
                break;
            case 'radio':
                input = document.querySelector(`[data-label="${field.name}"][value="${field.value}"]`);
                if (input) input.checked = true;
                break;
        }
    }

    for (const transaction of claimTransactions) {
        // Make sure the row exists (item-ids beyond the first need a row added).
        if (!ensureTransactionRow(transaction.item_id)) {
            console.error(
                `loadMember: no transaction table for "${transaction.name}" (item_id ${transaction.item_id}); skipping`
            );
            continue;
        }

        // Match by the saved key (prefixed + cleaned label) within the row,
        // since the rendered input's raw data-label won't equal transaction.name.
        const input = findTransactionInput(transaction.name, transaction.item_id);
        if (!input) {
            console.error(
                `loadMember: no rendered field for transaction "${transaction.name}" (item_id ${transaction.item_id}); skipping`
            );
            continue;
        }

        switch (input.type) {
            case 'text':
            case 'tel':
            case 'email':
            case 'number':
            case 'date':
            case 'select-one':
            case 'hidden':
                input.value = transaction.value;
                break;
            case 'textarea':
                input.innerText = transaction.value;
                break;
            case 'checkbox':
                input.checked = transaction.value.toUpperCase() === 'YES';
                break;
            case 'radio': {
                const radio = Array.from(
                    document.querySelectorAll(`[item-id="${transaction.item_id}"][value="${transaction.value}"]`)
                ).find((el) => transactionKey(el) === transaction.name);
                if (radio) radio.checked = true;
                break;
            }
        }
    }

    // Handle claim files for multiple documentation sections
    if (claimFiles.length > 0) {
        // Group files by their documentation section (doc_id)
        const filesBySection = {};
        
        for (const file of claimFiles) {
            const docId = getDocId(config, file.form_id);
            if (!filesBySection[docId]) {
                filesBySection[docId] = [];
            }
            filesBySection[docId].push(file);
        }

        // Process each documentation section
        for (const [docId, files] of Object.entries(filesBySection)) {
            const list = document.getElementById(`file-list-${docId}`);
            const noFilesElement = document.getElementById(`no-files-${docId}`);
            
            if (list && files.length > 0) {
                list.classList.remove('d-none');
                if (noFilesElement) {
                    noFilesElement.classList.add('d-none');
                }

                for (const file of files) {
                    const li = document.createElement('li');
                    li.setAttribute('disabled', 'disabled');
                    li.innerHTML = `<span class="float-right badge ${file.failed ? 'bg-danger' : 'bg-success'}">${file.failed ? 'FAILED' : 'Uploaded'}</span><span class="filename"><strong>${file.name}</strong></span><span class="description">(${file.description})</span>`;
                    list.appendChild(li);
                }
            }
        }

        // Fallback for legacy single file-list support
        const legacyList = document.getElementById('file-list');
        const legacyNoFiles = document.getElementById('no-files');
        
        if (legacyList && claimFiles.length > 0) {
            legacyList.classList.remove('d-none');
            if (legacyNoFiles) {
                legacyNoFiles.classList.add('d-none');
            }

            for (const file of claimFiles) {
                const li = document.createElement('li');
                li.setAttribute('disabled', 'disabled');
                li.innerHTML = `<span class="float-right badge ${file.failed ? 'bg-danger' : 'bg-success'}">${file.failed ? 'FAILED' : 'Uploaded'}</span><span class="filename"><strong>${file.name}</strong></span><span class="description">(${file.description})</span>`;
                legacyList.appendChild(li);
            }
        }
    }

    for (const key in member) {

        if (!member.hasOwnProperty(key)) continue;

        const field = document.getElementById(key);
        if (!field) continue;

        field.value = member[key];
    }

    const country = document.getElementById('country');
    if (country.value === '') country.value = 'USA';
    if (country.value !== 'USA') {
        document.querySelectorAll('#state,#zip_code').forEach((input) => {
            toggleFieldAttribute(input, 'required', false);
            input.parentNode.classList.add('d-none');
        });
        document.querySelectorAll('#province,#postal_code').forEach((input) => {
            toggleFieldAttribute(input, 'required', true);
            input.parentNode.classList.remove('d-none');
        });
        toggleForeignPhone(true);
    }

    // if (member.claim_id) $('#confirm_email_address').val($('#email_address').val());
    if (member.claim_id) document.getElementById('confirm_email_address').value = document.getElementById('email_address').value;
}

function getDocId(config, label) {
    // return the config key whose data_file_label matches the label
    for (const key in config) {
        if (config[key].data_file_label === label) {
            return key;
        }
    }
    return 'documentation';
}